import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { School, User, Download, Link as LinkIcon, BarChart3 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/input';
import { importApi } from '@/api/endpoints/import';
import { settingsApi, Setting } from '@/api/endpoints/settings';
import { participantsApi } from '@/api/endpoints/participants';
import { resultsApi } from '@/api/endpoints/results';
import { ADMIN_BASE } from '@/config/routes';
import toast from 'react-hot-toast';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const StatCard = ({
  title,
  value,
  icon: Icon,
  to,
}: {
  title: string;
  value: number | string;
  icon: LucideIcon;
  to?: string;
}) => (
  <Card>
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="mt-1 text-3xl font-bold text-gray-900">{value}</p>
          {to && (
            <Link to={to} className="mt-2 inline-block text-xs text-primary-600 hover:underline">
              Zobacz wszystkie →
            </Link>
          )}
        </div>
        <Icon className="h-10 w-10 text-competition-accent" />
      </div>
    </CardContent>
  </Card>
);

function isoToLocal(isoValue: string): string {
  const d = new Date(isoValue);
  const offset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offset).toISOString().slice(0, 16);
}

function fmtDuration(ms: number | null): string {
  if (ms === null) return '—';
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

interface StageTimePickerProps {
  label: string;
  settingKey: string;
  settings: Setting[] | undefined;
  onSave: (key: string, value: string) => void;
  isPending: boolean;
  hint: string;
}

const StageTimePicker = ({ label, settingKey, settings, onSave, isPending, hint }: StageTimePickerProps) => {
  const existing = settings?.find((s) => s.key === settingKey);
  const [localValue, setLocalValue] = useState('');

  useEffect(() => {
    if (existing?.value) {
      setLocalValue(isoToLocal(existing.value));
    }
  }, [existing?.value]);

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
      <div className="flex gap-2">
        <Input
          type="datetime-local"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
        />
        <Button
          onClick={() => {
            if (!localValue) return;
            onSave(settingKey, new Date(localValue).toISOString());
          }}
          disabled={isPending}
        >
          Zapisz
        </Button>
      </div>
      <p className="mt-1 text-xs text-gray-500">{hint}</p>
    </div>
  );
};

// ─── Top-N Qualify Modal ─────────────────────────────────────────────────────

interface QualifyModalProps {
  topN: number;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}

const QualifyModal = ({ topN, onClose, onConfirm, isPending }: QualifyModalProps) => {
  const { data: completers, isLoading } = useQuery({
    queryKey: ['combined-stage1-preview'],
    queryFn: () => resultsApi.getCombinedStage1(),
  });

  const preview = completers?.filter((c) => !c.disqualified).slice(0, topN) ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={isPending ? undefined : onClose} />
      <div className="relative z-10 w-full max-w-2xl rounded-lg bg-white shadow-xl flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Kwalifikacja Top {topN} do Etapu 2</h3>
            <p className="text-sm text-gray-500">
              Poniżej lista uczniów z obu terminów Etapu 1, którzy otrzymają token do Etapu 2.
              Sortowanie: wynik malejąco, czas od kliknięcia „Rozpocznij test” rosnąco.
            </p>
          </div>
          <button onClick={onClose} disabled={isPending} className="text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
        </div>

        <div className="overflow-y-auto flex-1 p-4">
          {isLoading ? (
            <LoadingSpinner size="md" className="py-8" />
          ) : preview.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">Brak uczniów z ukończonym Etapem 1.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs font-semibold text-gray-500 uppercase">
                  <th className="pb-2 pr-3">#</th>
                  <th className="pb-2 pr-3">Termin</th>
                  <th className="pb-2 pr-3">Uczeń</th>
                  <th className="pb-2 pr-3">Szkoła</th>
                  <th className="pb-2 pr-3 text-right">Wynik</th>
                  <th className="pb-2 text-right">Czas</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((c, i) => (
                  <tr key={c.sessionId ?? `${c.id}-${c.term ?? 1}`} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-gray-400">{i + 1}</td>
                    <td className="py-2 pr-3">
                      <span className="inline-flex rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                        Termin {c.term ?? 1}
                      </span>
                    </td>
                    <td className="py-2 pr-3 font-medium">{c.firstName} {c.lastName}</td>
                    <td className="py-2 pr-3 text-gray-500 text-xs">{c.school?.name ?? '—'}</td>
                    <td className="py-2 pr-3 text-right font-semibold text-primary-600">{c.score ?? '—'}</td>
                    <td className="py-2 text-right text-gray-500">{fmtDuration(c.durationFromStartClickMs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {completers && completers.filter((c) => !c.disqualified).length > topN && (
            <p className="mt-3 text-xs text-gray-400 text-center">
              Łącznie {completers.filter((c) => !c.disqualified).length} wyników z obu terminów Etapu 1 — zakwalifikowanych zostanie {topN}.
            </p>
          )}
        </div>

        <div className="border-t px-6 py-4 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={isPending}>Anuluj</Button>
          <Button onClick={onConfirm} disabled={isPending || preview.length === 0}>
            {isPending ? 'Kwalifikuję...' : `Potwierdź kwalifikację ${preview.length} uczniów`}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

export const DashboardPage = () => {
  const queryClient = useQueryClient();
  const [showQualifyModal, setShowQualifyModal] = useState(false);
  const [topN, setTopN] = useState(50);

  const { data: stats, isLoading } = useQuery({
    queryKey: ['import-stats'],
    queryFn: importApi.getStats,
  });

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.getSettings,
  });

  const updateSetting = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => settingsApi.updateSetting(key, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Zapisano ustawienia');
    },
    onError: () => toast.error('Błąd zapisu'),
  });

  const autoPromote = useMutation({
    mutationFn: () => participantsApi.autoPromoteTopN(topN),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['magic-links'] });
      queryClient.invalidateQueries({ queryKey: ['stage-completers'] });
      setShowQualifyModal(false);
      const clearedMsg = result.cleared > 0 ? ` Usunięto ${result.cleared} poprzednich kwalifikacji.` : '';
      toast.success(`Zakwalifikowano ${result.promoted} uczniów do Etapu 2.${clearedMsg}`);
    },
    onError: () => toast.error('Błąd kwalifikacji'),
  });

  const resetStage = useMutation({
    mutationFn: ({ stage, term }: { stage: number; term?: number }) =>
      participantsApi.resetStageForTesting(stage, term),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['magic-links'] });
      queryClient.invalidateQueries({ queryKey: ['combined-stage1-preview'] });
      queryClient.invalidateQueries({ queryKey: ['combined-stage1'] });
      queryClient.invalidateQueries({ queryKey: ['stage-completers'] });
      const termStr = result.term ? ` Termin ${result.term}` : '';
      const reEnrolledMsg =
        result.reEnrolled != null && result.reEnrolled > 0
          ? ` Przywrócono ${result.reEnrolled} uczestników na liście.`
          : '';
      toast.success(
        `[DEV] Usunięto ${result.deleted} sesji dla Etapu ${result.stage}${termStr}.${reEnrolledMsg}`,
      );
    },
    onError: () => toast.error('Błąd resetowania'),
  });

  if (isLoading) {
    return <LoadingSpinner size="lg" className="mt-20" />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
        <p className="mt-1 text-sm text-gray-500">Przegląd turnieju przetwórstwa mleczarskiego</p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        <StatCard title="Szkoły" value={stats?.schools ?? 0} icon={School} to="/schools" />
        <StatCard title="Uczestnicy" value={stats?.participants ?? 0} icon={User} to="/participants" />
        <StatCard title="Zaimportowane wpisy" value={stats?.imports ?? 0} icon={Download} to="/import" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Competition settings */}
        <Card>
          <CardHeader>
            <CardTitle>Ustawienia Turnieju</CardTitle>
            <p className="text-sm text-gray-500">Ustaw oddzielne czasy startu dla każdego etapu.</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-5">
              <StageTimePicker
                label="Etap 1 — Termin 1: data i godzina startu"
                settingKey="stageStartTime_1"
                settings={settings}
                onSave={(key, value) => updateSetting.mutate({ key, value })}
                isPending={updateSetting.isPending}
                hint="Czas odliczania w Poczekalni dla pierwszego terminu Etapu 1."
              />
              <StageTimePicker
                label="Etap 1 — Termin 2: data i godzina startu"
                settingKey="stageStartTime_1_2"
                settings={settings}
                onSave={(key, value) => updateSetting.mutate({ key, value })}
                isPending={updateSetting.isPending}
                hint="Czas odliczania w Poczekalni dla drugiego terminu Etapu 1."
              />
              <StageTimePicker
                label="Etap 2 — Ogólnopolski: data i godzina startu"
                settingKey="stageStartTime_2"
                settings={settings}
                onSave={(key, value) => updateSetting.mutate({ key, value })}
                isPending={updateSetting.isPending}
                hint="Czas odliczania w Poczekalni dla etapu ogólnopolskiego."
              />
            </div>
          </CardContent>
        </Card>

        {/* Qualify Top-N */}
        <Card className="border-competition-accent/30 bg-competition-accent/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-competition-accent-dark">
              Kwalifikacja do Etapu 2
            </CardTitle>
            <p className="text-sm text-competition-accent-dark/80">
              Automatycznie wybierz najlepszych uczniów z obu terminów Etapu 1 i przyznaj im tokeny do Etapu 2.
              Sortowanie: wynik malejąco, czas od kliknięcia „Rozpocznij test” rosnąco (remisy).
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-competition-accent-dark">
                Liczba kwalifikowanych uczniów
              </label>
              <div className="flex gap-3 items-center">
                <Input
                  type="number"
                  min={1}
                  max={500}
                  value={topN}
                  onChange={(e) => setTopN(Math.max(1, Math.min(500, Number(e.target.value))))}
                  className="w-28"
                />
                <Button
                  onClick={() => setShowQualifyModal(true)}
                  disabled={autoPromote.isPending}
                >
                  Zakończ Etap 1 i zakwalifikuj Top {topN}
                </Button>
              </div>
              <p className="mt-1 text-xs text-competition-accent-dark/70">
                Podgląd listy pojawi się przed potwierdzeniem.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Dev test mode */}
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              Tryb testowy
            </CardTitle>
            <p className="text-sm text-amber-700">
              Narzędzia do szybkiego testowania etapów. Ustaw start na <strong>teraz</strong> lub
              zresetuj sesje danego etapu, żeby zacząć od nowa.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Button
                className="justify-start gap-2"
                onClick={() => {
                  const now = new Date().toISOString();
                  updateSetting.mutate(
                    { key: 'stageStartTime_1', value: now },
                    { onSuccess: () => toast.success('Etap 1 Termin 1 — start ustawiony na teraz') },
                  );
                }}
                disabled={updateSetting.isPending}
              >
                Uruchom Etap 1 T1 teraz
              </Button>
              <Button
                className="justify-start gap-2"
                onClick={() => {
                  const now = new Date().toISOString();
                  updateSetting.mutate(
                    { key: 'stageStartTime_1_2', value: now },
                    { onSuccess: () => toast.success('Etap 1 Termin 2 — start ustawiony na teraz') },
                  );
                }}
                disabled={updateSetting.isPending}
              >
                Uruchom Etap 1 T2 teraz
              </Button>
              <Button
                className="justify-start gap-2"
                onClick={() => {
                  const now = new Date().toISOString();
                  updateSetting.mutate(
                    { key: 'stageStartTime_2', value: now },
                    { onSuccess: () => toast.success('Etap 2 — start ustawiony na teraz') },
                  );
                }}
                disabled={updateSetting.isPending}
              >
                Uruchom Etap 2 teraz
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="danger"
                className="justify-start gap-2"
                onClick={() => {
                  if (confirm('Na pewno usunąć WSZYSTKIE sesje Etapu 1 Termin 1? Tej operacji nie można cofnąć.')) {
                    resetStage.mutate({ stage: 1, term: 1 });
                  }
                }}
                disabled={resetStage.isPending}
              >
                Resetuj sesje E1 T1
              </Button>
              <Button
                variant="danger"
                className="justify-start gap-2"
                onClick={() => {
                  if (confirm('Na pewno usunąć WSZYSTKIE sesje Etapu 1 Termin 2? Tej operacji nie można cofnąć.')) {
                    resetStage.mutate({ stage: 1, term: 2 });
                  }
                }}
                disabled={resetStage.isPending}
              >
                Resetuj sesje E1 T2
              </Button>
              <Button
                variant="danger"
                className="justify-start gap-2"
                onClick={() => {
                  if (confirm('Na pewno usunąć WSZYSTKIE sesje Etapu 2? Tej operacji nie można cofnąć.')) {
                    resetStage.mutate({ stage: 2 });
                  }
                }}
                disabled={resetStage.isPending}
              >
                Resetuj sesje Etap 2
              </Button>
            </div>
            <p className="text-xs text-amber-600">
              Reset usuwa sesje (tokeny, odpowiedzi, wyniki) — tokeny trzeba wygenerować ponownie
              w zakładce Magic Linki.
            </p>
          </CardContent>
        </Card>

        {/* Quick actions */}
        <Card>
          <CardHeader>
            <CardTitle>Szybkie akcje</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/import" className="block">
              <Button className="w-full justify-start gap-3" variant="secondary">
                <Download className="h-4 w-4" />
                Importuj zgłoszenia z JSON
              </Button>
            </Link>
            <Link to="/schools" className="block">
              <Button className="w-full justify-start gap-3" variant="secondary">
                <School className="h-4 w-4" />
                Przeglądaj szkoły i uczestników
              </Button>
            </Link>
            <Link to={`${ADMIN_BASE}/qualification`} className="block">
              <Button className="w-full justify-start gap-3" variant="secondary">
                <BarChart3 className="h-4 w-4" />
                Kwalifikacja do Etapu 2
              </Button>
            </Link>
            <Link to="/magic-links" className="block">
              <Button className="w-full justify-start gap-3" variant="secondary">
                <LinkIcon className="h-4 w-4" />
                Magic Linki (etap 1 i 2)
              </Button>
            </Link>
            <Link to="/leaderboard" className="block">
              <Button className="w-full justify-start gap-3" variant="secondary">
                <BarChart3 className="h-4 w-4" />
                Tabela liderów
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {showQualifyModal && (
        <QualifyModal
          topN={topN}
          onClose={() => setShowQualifyModal(false)}
          onConfirm={() => autoPromote.mutate()}
          isPending={autoPromote.isPending}
        />
      )}
    </div>
  );
};
